import * as React from "react";
import Link from "./link.js";
import LangChange from "./langChange.js";
import { GatsbyImage } from "gatsby-plugin-image";
import styled from "styled-components";
import PageContainer from "./pageContainer.js";
import { when } from "../styles/theme.js";
import { useState } from "react";
import MobileMenu from "./mobileMenu.js";
import { BsChevronDown } from "react-icons/bs";
import { useLocalization } from "gatsby-theme-i18n";

const Container = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${(props) => props.theme.zIndex.header};
  transition: background-color 300ms ease-in-out;
  background-color: ${(props) =>
    props.atTop ? "transparent" : "rgba(0,0,0,0.3)"};

  ${when("lg")} {
    position: absolute;
    background-color: transparent;
  }
`;

const LogoContainer = styled(Link)`
  width: 13.6rem;
  display: block;
`;

const MenuBar = styled.div`
  display: flex;
  align-items: flex-end;
  height: 8rem;
  padding-bottom: 1rem;

  ${when("lg")} {
    align-items: center;
    padding-bottom: 0;
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const LgMenu = styled.div`
  display: none;
  color: ${(props) => props.theme.colors.white};

  ${when("lg")} {
    display: flex;
    align-items: center;
  }
`;

const MainNav = styled.nav`
  //padding: 1rem;
  display: flex;
  align-items: center;
`;

const MainNavLink = styled(Link)`
  padding: 1rem 2rem;
  position: relative;
  font-size: 1.4rem;

  ${when("xl")} {
    font-size: 1.6rem;
  }

  &:after {
    content: "";
    position: absolute;
    left: 2rem;
    width: 0;
    bottom: 0.5rem;
    border-bottom: 1px solid ${(props) => props.theme.colors.white};
    transition: width 250ms ease-in-out;
  }

  &:hover {
    &:after {
      width: calc(100% - 4rem);
    }
  }

  &.active {
    &:after {
      width: calc(100% - 4rem);
    }
  }
`;

const BONavLink = styled(MainNavLink)`
  display: flex;
  align-items: center;
`;

const BurgerMenuContainer = styled.div`
  position: fixed;
  top: 8rem;
  right: ${(props) => props.theme.pageContainerWidth.xs - 1}rem;
  z-index: ${(props) => props.theme.zIndex.burger};
  transform: translateY(-100%);
  padding-bottom: 1rem;

  ${when("sm")} {
    right: ${(props) => props.theme.pageContainerWidth.sm - 1}rem;
  }

  ${when("md")} {
    right: ${(props) => props.theme.pageContainerWidth.md - 1}rem;
  }

  ${when("lg")} {
    display: none;
  }
`;

const BurgerContainer = styled.div`
  padding: 1rem;
  display: inline-block;
  cursor: pointer;
  transition-property: opacity, filter;
  transition-duration: 0.15s;
  transition-timing-function: linear;
  font: inherit;
  color: inherit;
  text-transform: none;
  background-color: transparent;
  border: 0;
  margin: 0;
  overflow: visible;
  transition: opacity 300ms ease-in-out;

  &:hover {
    opacity: 0.5;
  }
`;

const BurgerInnerContainer = styled.div`
  width: 3rem;
  height: 2.4rem;
  display: inline-block;
  position: relative;
`;

const Burger = styled.div`
  display: block;
  margin-top: -0.2rem;
  width: 3rem;
  height: 0.2rem;
  background-color: #fff;
  border-radius: 0.2rem;
  position: absolute;
  transition-property: transform;
  top: auto;
  bottom: 0;
  transform: ${(props) =>
    props.isActive ? "translate3d(0, -0.6rem, 0) rotate(-45deg)" : "none"};
  transition-duration: ${(props) => (props.isActive ? "0.22s" : "0.13s")};
  transition-delay: 0.13s;
  transition-timing-function: ${(props) =>
    props.isActive
      ? "cubic-bezier(0.215, 0.61, 0.355, 1)"
      : "cubic-bezier(0.55, 0.055, 0.675, 0.19)"};

  &:before {
    width: 3rem;
    height: 0.2rem;
    background-color: #fff;
    border-radius: 0.2rem;
    position: absolute;
    transition-property: transform;
    transition-duration: 0.15s;
    transition-timing-function: ease;
    content: "";
    display: block;
    top: ${(props) => (props.isActive ? "0" : "-1.25rem")};
    transform: ${(props) => (props.isActive ? "rotate(-90deg)" : "none")};
    transition: ${(props) =>
      props.isActive
        ? "top 0.1s 0.16s cubic-bezier(0.33333, 0, 0.66667, 0.33333), transform 0.13s 0.25s cubic-bezier(0.215, 0.61, 0.355, 1)"
        : "top 0.12s 0.2s cubic-bezier(0.33333, 0.66667, 0.66667, 1), transform 0.13s cubic-bezier(0.55, 0.055, 0.675, 0.19)"};
  }
`;

const LangChangeButton = styled(LangChange)`
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${(props) => props.theme.colors.white};
  border-radius: 999px;
  margin-left: 1rem;
  font-size: 1.4rem;
`;

const GlobalNavDropDown = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0.5rem;
  text-align: center;
  display: none;
`;

const GlobalNav = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  cursor: pointer;
  position: relative;
  font-size: 1.4rem;

  &:hover {
    ${GlobalNavDropDown} {
      display: block;
    }
  }

  ${when("xl")} {
    font-size: 1.6rem;
  }
`;

const GlobalNavIcon = styled(BsChevronDown)`
  display: block;
  margin-left: 0.25rem;
`;

const SubNav = styled.div`
  display: none;
  position: absolute;
  left: 80%;
  top: 0;
  width: 100;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0.5rem;
  text-align: center;
  min-width: 20rem;
`;

const GlobalNavLinkContainer = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.white};
  position: relative;

  &:first-child {
    border-top: none;
  }
`;

const GlobalNavLinkContainerSub = styled(GlobalNavLinkContainer)`
  &:hover {
    ${SubNav} {
      display: block;
    }
  }
`;

const GlobalNavLink = styled.a`
  display: block;
  padding: 0.5rem 1rem;
`;

const BrandNavLink = styled(Link)`
  display: block;
  padding: 0.5rem 1rem;
  opacity: 0.8;
`;

const BrandNavLinkBold = styled(BrandNavLink)`
  font-weight: bold;
  opacity: 1;
`;

const Header = ({
  mainNav,
  globalNav,
  originalPath,
  settings,
  atTop,
  brands,
}) => {
  const [isMenuActive, setIsMenuActive] = useState(false);
  const { locale } = useLocalization();

  return (
    <>
      <BurgerMenuContainer>
        <BurgerContainer
          onClick={() => {
            setIsMenuActive(!isMenuActive);
          }}
        >
          <BurgerInnerContainer>
            <Burger isActive={isMenuActive} />
          </BurgerInnerContainer>
        </BurgerContainer>
      </BurgerMenuContainer>
      <Container atTop={atTop}>
        <PageContainer>
          <MenuBar>
            {settings?.logo && (
              <LogoContainer href={"/"}>
                <GatsbyImage
                  alt={settings.logo.description}
                  image={settings.logo.gatsbyImageData}
                />
              </LogoContainer>
            )}
            <Spacer />
            <LgMenu>
              <MainNav>
                {mainNav?.links.map((nav, index) => {
                  if (nav.url.includes("business-overview")) {
                    return (
                      <GlobalNav key={index}>
                        <BONavLink
                          href={nav.url}
                          activeClassName={"active"}
                          partiallyActive={nav.url !== "/"}
                        >
                          {nav.title}
                          <GlobalNavIcon />
                        </BONavLink>
                        <GlobalNavDropDown>
                          <GlobalNavLinkContainer>
                            <BrandNavLinkBold
                              href={
                                locale === "en"
                                  ? "/en/business-overview#central-kitchen"
                                  : "/business-overview#central-kitchen"
                              }
                            >
                              {locale === "en" ? "Central Kitchen" : "中央廚房"}
                            </BrandNavLinkBold>
                          </GlobalNavLinkContainer>
                          <GlobalNavLinkContainer>
                            <BrandNavLinkBold
                              href={
                                locale === "en"
                                  ? "/en/business-overview#packing-products"
                                  : "/business-overview#packing-products"
                              }
                            >
                              {locale === "en"
                                ? "Packing Products"
                                : "包裝產品"}
                            </BrandNavLinkBold>
                          </GlobalNavLinkContainer>
                          <GlobalNavLinkContainerSub>
                            <BrandNavLinkBold
                              href={
                                locale === "en"
                                  ? "/en/business-overview#brands"
                                  : "/business-overview#brands"
                              }
                            >
                              {locale === "en" ? "Restaurants" : "旗下餐廳"}
                            </BrandNavLinkBold>
                            <SubNav>
                              {brands.map((brand, index) => {
                                const link =
                                  locale === "en"
                                    ? `/en/restaurants/${brand.slug}`
                                    : `/restaurants/${brand.slug}`;

                                return (
                                  <GlobalNavLinkContainer key={index}>
                                    <BrandNavLink href={link}>
                                      {brand.name}
                                    </BrandNavLink>
                                  </GlobalNavLinkContainer>
                                );
                              })}
                            </SubNav>
                          </GlobalNavLinkContainerSub>
                        </GlobalNavDropDown>
                      </GlobalNav>
                    );
                  }
                  return (
                    <MainNavLink
                      href={nav.url}
                      key={index}
                      activeClassName={"active"}
                      partiallyActive={nav.url !== "/"}
                    >
                      {nav.title}
                    </MainNavLink>
                  );
                })}
              </MainNav>
              <GlobalNav>
                {globalNav?.title}
                <GlobalNavIcon />
                <GlobalNavDropDown>
                  {globalNav?.links.map((link, index) => {
                    return (
                      <GlobalNavLinkContainer key={index}>
                        <GlobalNavLink href={link.url} target={"_blank"}>
                          {link.title}
                        </GlobalNavLink>
                      </GlobalNavLinkContainer>
                    );
                  })}
                </GlobalNavDropDown>
              </GlobalNav>
              <LangChangeButton originalPath={originalPath} />
            </LgMenu>
          </MenuBar>
        </PageContainer>
      </Container>
      <MobileMenu
        isActive={isMenuActive}
        setIsActive={setIsMenuActive}
        mainNav={mainNav}
        globalNav={globalNav}
        originalPath={originalPath}
        brands={brands}
      />
    </>
  );
};

export default Header;
